import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, Mail } from 'lucide-react';
import { companyInfo } from '../data/mock';

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileView, setIsMobileView] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Treat small viewports as "scrolled" so the navbar is opaque on phones by default
    const mq = window.matchMedia('(max-width: 1023px)');
    const handler = (e) => setIsMobileView(e.matches);
    setIsMobileView(mq.matches);

    if (mq.addEventListener) {
      mq.addEventListener('change', handler);
    } else {
      mq.addListener(handler);
    }

    return () => {
      if (mq.removeEventListener) {
        mq.removeEventListener('change', handler);
      } else {
        mq.removeListener(handler);
      }
    };
  }, []);

  const shouldOpaque = isScrolled || isMobileView;

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'About' },
    { path: '/services', label: 'Services' },
    { path: '/process', label: 'Process' },
    // { path: '/price-trends', label: 'Price Trends' },
    { path: '/certificates', label: 'Certificates' },
    { path: '/contact', label: 'Contact' }
  ];

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      shouldOpaque ? 'bg-background/95 backdrop-blur-sm shadow-lg' : 'bg-transparent'
    }`}>
      {/* Top Bar */}
      <div className="bg-background py-2 px-4">
        <div className="container mx-auto flex justify-between items-center text-sm">
          <div className="flex gap-4">
            <a href={`tel:${companyInfo.mobile}`} className="flex items-center gap-2 text-foreground hover:text-accent transition-colors">
              <Phone size={14} />
              <span className="hidden sm:inline">{companyInfo.mobile}</span>
            </a>
            <a href={`mailto:${companyInfo.email}`} className="flex items-center gap-2 text-foreground hover:text-accent transition-colors">
              <Mail size={14} />
              <span className="hidden sm:inline">{companyInfo.email}</span>
            </a>
          </div>
          <div className="text-muted text-xs hidden md:block">
            Govt. Registered & Approved Valuer
          </div>
        </div>
      </div>

      {/* Main Navigation */}
      <nav className="container mx-auto px-4 py-4">
        <div className="flex justify-between items-center">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <img
              src="/MehtaAssociatesLogo.png"
              alt={`${companyInfo.name} logo`}
              className="w-12 h-12 sm:w-20 sm:h-20 md:w-24 md:h-24 lg:w-28 lg:h-28 object-contain flex-shrink-0"
            />
            <div className="flex flex-col leading-tight">
              <span className="text-xl sm:text-2xl font-bold text-foreground whitespace-nowrap">
                {companyInfo.name}
              </span>
              <span className="text-xs text-muted">{companyInfo.tagline}</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`nav-link text-sm px-4 py-2 ${
                    location.pathname === link.path 
                    ? 'text-foreground border-b-2 border-primary' 
                    : 'text-muted hover:text-accent'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden text-foreground p-2"
            aria-label="Toggle menu"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
        
        {isOpen && (
          <div className="lg:hidden mt-4 pb-4 border-t border-border pt-4">
            <div className="flex flex-col gap-2">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsOpen(false)}
                  className={`nav-link text-sm px-4 py-3 ${
                        location.pathname === link.path 
                          ? 'text-foreground border-b-2 border-primary' 
                          : 'text-muted hover:text-accent'
                      }`}
                >
                  {link.label}
                </Link>
              ))}
              <Link
                to="/contact"
                onClick={() => setIsOpen(false)}
                className="btn-primary mt-4"
              >
                Get Valuation
              </Link>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;
